import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { PageHero } from "../components/shared/Primitives";
import { CheckCircle2, ShieldCheck, AlertTriangle, ArrowRight, Sparkles } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { useHandoverSession } from "../hooks/useHandoverSession";

// Native Stripe checkout for the two migrated paid tiers — Pro Carer
// Circle and Family Circle. Deliberately mirrors MembershipCarerProfile
// .jsx's proven pattern (that page is untouched — a separate, already
// -working flow) rather than introducing a new UI convention.
const PLAN_CONFIG = {
  "pro-carer-circle": {
    planId: "pro_carer_monthly",
    label: "Pro Carer Office",
    accent: "purple",
    priceLine: "£19 / month · 7-day free trial, cancel any time",
    ctaLabel: "Start 7-day free trial",
    manageLink: "/workspace/pro-carer-circle",
    manageCta: "Go to my Pro Carer Office",
    alreadyEntitlement: "pro_carer",
    requiresCarerDeclaration: true,
    included: [
      "Full Pro Carer toolkit — templates, calculators, CPD guidance",
      "Carer Essentials included — documents, expiries, CPD, references & work record",
      "Tax & self-employment guidance for independent carers",
    ],
    btnClass: "btn-purple",
  },
  "family-circle": {
    planId: "family_circle_monthly",
    label: "Family Care Office",
    accent: "teal",
    priceLine: "£19 / month · cancel any time",
    ctaLabel: "Continue to secure checkout",
    manageLink: "/workspace/family-circle",
    manageCta: "Go to my Family Care Office",
    alreadyEntitlement: "family_circle",
    included: [
      "Full agreements, checklists and pricing guidance for arranging care",
      "Everything already included in Family Starter — free",
      "Direct access to the Carer Profile directory",
      "Priority support publishing a Care Need",
    ],
    btnClass: "btn-teal",
  },
};

export default function PaidMembershipCheckout() {
  const { planSlug } = useParams();
  const config = PLAN_CONFIG[planSlug];
  const { ready, active, email: sessionEmail, entitlements } = useHandoverSession();
  const alreadyHasIt = ready && active && config && entitlements.includes(config.alreadyEntitlement);

  const [form, setForm] = useState({ name: "", firstName: "", lastName: "", email: "", declarationAccepted: false });
  const [status, setStatus] = useState({ loading: false, error: "" });
  const [blocked, setBlocked] = useState(null);

  useEffect(() => {
    if (ready && active && sessionEmail) {
      setForm((f) => (f.email ? f : { ...f, email: sessionEmail }));
    }
  }, [ready, active, sessionEmail]);

  if (!config) {
    return (
      <div className="container-jcc py-24 text-center" data-testid="paid-checkout-unknown-plan">
        <h1 className="font-display text-2xl font-bold">Membership not found</h1>
      </div>
    );
  }

  const setField = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const canSubmit = form.email && (
    !config.requiresCarerDeclaration || (form.firstName.trim() && form.lastName.trim() && form.declarationAccepted)
  );

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setStatus({ loading: true, error: "" });
    setBlocked(null);
    try {
      const { data } = await api.post("/api/membership/checkout", {
        plan_id: config.planId,
        email: form.email,
        name: config.requiresCarerDeclaration
          ? `${form.firstName.trim()} ${form.lastName.trim()}`.trim()
          : (form.name || undefined),
        first_name: config.requiresCarerDeclaration ? form.firstName.trim() : undefined,
        last_name: config.requiresCarerDeclaration ? form.lastName.trim() : undefined,
        declaration_accepted: config.requiresCarerDeclaration ? form.declarationAccepted : undefined,
        origin_url: window.location.origin,
      });
      if (data.blocked) {
        setBlocked(data);
        setStatus({ loading: false, error: "" });
        return;
      }
      window.location.assign(data.checkout_url);
    } catch (err) {
      setStatus({ loading: false, error: formatApiError(err.response?.data?.detail) || err.message });
    }
  };

  if (!ready) {
    return <div className="container-jcc py-24 text-center text-ink-muted" data-testid={`page-membership-${planSlug}-loading`}>Loading…</div>;
  }

  if (alreadyHasIt) {
    return (
      <div data-testid={`page-membership-${planSlug}`}>
        <PageHero
          eyebrow={config.label}
          title="You're already covered."
          description={`You already have ${config.label} — no need to pay again.`}
          accent={config.accent}
        />
        <section className="section">
          <div className="container-jcc max-w-xl">
            <div className="card-base p-8 text-center" data-testid="paid-checkout-already-member">
              <Sparkles className={`w-10 h-10 mx-auto ${config.accent === "purple" ? "text-purple" : "text-teal"}`} />
              <h2 className="mt-4 font-display text-2xl font-bold">Already a {config.label} member?</h2>
              <p className="mt-3 text-ink-muted leading-relaxed">
                Manage your membership, billing and cancellation from your account.
              </p>
              <Link to={config.manageLink} className={`${config.btnClass} mt-6 inline-flex`} data-testid="paid-checkout-manage-cta">
                {config.manageCta} <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div data-testid={`page-membership-${planSlug}`}>
      <PageHero
        eyebrow={config.label}
        title={`Join ${config.label} — ${config.priceLine.split(" · ")[0]}.`}
        description={config.priceLine}
        accent={config.accent}
      />
      <section className="section">
        <div className="container-jcc space-y-8">
          {ready && !active && (
            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-5 flex flex-col sm:flex-row sm:items-center gap-3 sm:justify-between" data-testid="paid-checkout-signin-nudge">
              <p className="text-sm text-ink-muted">Already a JCC member? Sign in first for a faster checkout.</p>
              <Link to={`/auth/handover/request?next=/membership/${planSlug}`} className="btn-outline shrink-0 text-sm py-2" data-testid="paid-checkout-signin-link">
                Sign in
              </Link>
            </div>
          )}
          <div className="grid lg:grid-cols-5 gap-10">
            <div className="lg:col-span-2 space-y-6">
              <div className="card-base p-6">
                <div className={`flex items-center gap-2 font-semibold ${config.accent === "purple" ? "text-purple" : "text-teal"}`}>
                  <ShieldCheck className="w-5 h-5" /> {config.priceLine}
                </div>
                {config.requiresCarerDeclaration && (
                  <p className="mt-2 text-xs font-semibold text-purple">Individual carer membership only</p>
                )}
                <p className="mt-2 text-sm text-ink-muted">
                  Secure checkout via Stripe. Cancel any time from your account — you'll keep access until the end of
                  the period you've already paid for.
                </p>
              </div>
              <div className="rounded-2xl border border-slate-200 bg-white p-6">
                <p className="text-sm font-semibold text-ink mb-3">What's included</p>
                <ul className="space-y-2.5">
                  {config.included.map((it) => (
                    <li key={it} className="flex gap-2.5 text-sm text-ink-muted leading-relaxed">
                      <CheckCircle2 className={`w-4 h-4 mt-0.5 shrink-0 ${config.accent === "purple" ? "text-purple" : "text-teal"}`} strokeWidth={1.75} />
                      <span>{it}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <Link to={`/compare-memberships#${config.accent === "purple" ? "carer-memberships" : "family-memberships"}`} className="block text-center text-xs text-ink-soft hover:text-ink underline" data-testid="paid-checkout-compare-link">
                Compare all memberships
              </Link>
            </div>
            <form onSubmit={onSubmit} className="lg:col-span-3 card-base p-8 space-y-5" data-testid="paid-checkout-form">
              {config.requiresCarerDeclaration ? (
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium mb-1.5">First name</label>
                    <input
                      required
                      value={form.firstName}
                      onChange={setField("firstName")}
                      data-testid="paid-checkout-first-name"
                      className="w-full rounded-xl border border-slate-200 px-4 py-3 focus:outline-none focus:ring-1 focus:ring-slate-900"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1.5">Last name</label>
                    <input
                      required
                      value={form.lastName}
                      onChange={setField("lastName")}
                      data-testid="paid-checkout-last-name"
                      className="w-full rounded-xl border border-slate-200 px-4 py-3 focus:outline-none focus:ring-1 focus:ring-slate-900"
                    />
                  </div>
                </div>
              ) : (
                <div>
                  <label className="block text-sm font-medium mb-1.5">Your name</label>
                  <input
                    required
                    value={form.name}
                    onChange={setField("name")}
                    data-testid="paid-checkout-name"
                    className="w-full rounded-xl border border-slate-200 px-4 py-3 focus:outline-none focus:ring-1 focus:ring-slate-900"
                  />
                </div>
              )}
              <div>
                <label className="block text-sm font-medium mb-1.5">Email address</label>
                <input
                  required
                  type="email"
                  value={form.email}
                  onChange={setField("email")}
                  readOnly={ready && active && !!sessionEmail}
                  data-testid="paid-checkout-email"
                  className="w-full rounded-xl border border-slate-200 px-4 py-3 focus:outline-none focus:ring-1 focus:ring-slate-900 disabled:bg-slate-50"
                />
              </div>
              {config.requiresCarerDeclaration && (
                <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                  <label className="flex items-start gap-3 text-sm cursor-pointer">
                    <input
                      type="checkbox"
                      required
                      checked={form.declarationAccepted}
                      onChange={(e) => setForm({ ...form, declarationAccepted: e.target.checked })}
                      data-testid="paid-checkout-declaration-checkbox"
                      className="mt-0.5 w-4 h-4"
                    />
                    <span>
                      I confirm that I am registering as an individual carer in my own name. I am not registering
                      on behalf of a care agency, care provider, recruitment business or other organisation.
                    </span>
                  </label>
                  <p className="mt-2 text-xs text-ink-muted">
                    Carer accounts are for individual carers only. Business and organisational accounts will be removed.
                  </p>
                </div>
              )}
              {blocked && (
                <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800 flex gap-2" data-testid="paid-checkout-blocked">
                  <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                  <div>
                    <span>{blocked.message}</span>
                    {blocked.reason !== "identity_review_required" && (
                      <Link to="/account/billing" className="block mt-1 font-medium underline">Manage my membership</Link>
                    )}
                  </div>
                </div>
              )}
              {status.error && (
                <div className="rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-700 flex gap-2" data-testid="paid-checkout-error">
                  <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                  <span>{status.error}</span>
                </div>
              )}
              <button disabled={status.loading || !canSubmit} className={`${config.btnClass} w-full md:w-auto disabled:opacity-50`} data-testid="paid-checkout-submit">
                {status.loading ? "Redirecting to secure checkout…" : config.ctaLabel}
              </button>
              <p className="text-xs text-ink-soft">
                You'll be taken to Stripe's secure checkout page to complete payment. No card details are ever stored
                by Just Care Community.
              </p>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
}
